import React from 'react';
import { UserContext } from '@mergermarket/acuris-react-user-context';
import moment from 'moment-timezone';
import { formatIntelCount } from '@mergermarket/acuris-react-frontend-utils';
import styled, { css } from 'styled-components';
import { fontFamily, warmGrey, breakpoints } from '@mergermarket/acuris-react-styles';

const HeaderLabelDate = ({
  date
}) => {
  const {
    userTimeZone
  } = React.useContext(UserContext);
  const publishTimeWithTimezone = moment(date).tz(userTimeZone);
  return /*#__PURE__*/React.createElement("time", {
    dateTime: date
  }, publishTimeWithTimezone.format('DD/MM/YYYY'));
};

const HeaderLabelText = ({
  text,
  count,
  shouldShowZeros = false
}) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, text, (count !== undefined && shouldShowZeros || count && count > 0) && /*#__PURE__*/React.createElement("span", null, " (", formatIntelCount(count), ")"));
};

let ELayout;

(function (ELayout) {
  ELayout["TwoColLayout"] = "2ColLayout";
  ELayout["ThreeColLayout"] = "3ColLayout";
})(ELayout || (ELayout = {}));

const Headline = ({
  isInDropdown,
  children,
  className
}) => isInDropdown ? /*#__PURE__*/React.createElement("h2", {
  className: className,
  "data-test-section-header-text": true
}, children) : /*#__PURE__*/React.createElement("h3", {
  className: className,
  "data-test-section-header-text": true
}, children);
/**
 *
 * @param layout 2ColLayout or 3ColLayout
 * @param screenSize
 *
 * screenSize for 2 column design - medium or large
 * screenSize for 3 column design - xlarge or xxlarge
 */


const column2Position = (layout, screenSize) => {
  const DECORATIVE_LINE_WIDTH = 72;
  const breakpointLayout = {
    [ELayout.TwoColLayout]: {
      medium: 25,
      large: 25,
      xlarge: 20
    },
    [ELayout.ThreeColLayout]: {
      medium: 25,
      large: 25,
      xlarge: 20
    }
  };
  const breakpointTranslationToGrid = {
    [ELayout.TwoColLayout]: {
      medium: 8,
      large: 8,
      xlarge: 1
    },
    [ELayout.ThreeColLayout]: {
      medium: 8,
      large: 8,
      xlarge: 1
    }
  }; // @ts-ignore

  const left = breakpointLayout[layout][screenSize]; // @ts-ignore

  const translateX = DECORATIVE_LINE_WIDTH + breakpointTranslationToGrid[layout][screenSize];
  return `calc(${left}% - ${translateX}px)`;
};

const columnAlignmentLayout = columnBreakpoints => {
  const {
    twoColumnAtBreakpoint,
    threeColumnAtBreakpoint
  } = columnBreakpoints;
  let hasColumnLayout = false;
  let breakpointFor2ColumnLayout = null;
  let breakpointFor3ColumnLayout = null;

  if (twoColumnAtBreakpoint) {
    breakpointFor2ColumnLayout = twoColumnAtBreakpoint;
    breakpointFor3ColumnLayout = threeColumnAtBreakpoint || 'xlarge';
    hasColumnLayout = true;
  } else if (threeColumnAtBreakpoint) {
    breakpointFor3ColumnLayout = threeColumnAtBreakpoint;
    breakpointFor2ColumnLayout = twoColumnAtBreakpoint || 'medium';
    hasColumnLayout = true;
  }

  if (!breakpointFor2ColumnLayout || !breakpointFor3ColumnLayout) {
    return css``;
  }

  const breakpoints3Columns = breakpoints[breakpointFor3ColumnLayout];
  const breakpoints2Columns = breakpoints[breakpointFor2ColumnLayout];
  return hasColumnLayout && css`
      @media screen and (min-width: ${breakpoints2Columns}) {
        text-align: left;
        &:before,
        &:after {
          width: 7.2rem;
        }

        &:before {
          margin-left: ${column2Position(ELayout.TwoColLayout, breakpointFor2ColumnLayout)};
        }
      }

      ${breakpointFor3ColumnLayout === 'xxlarge' && css`
          @media screen and (min-width: ${breakpoints.xlarge}) {
            text-align: left;
            &:before,
            &:after {
              width: 7.2rem;
            }

            &:before {
              margin-left: ${column2Position(ELayout.TwoColLayout, 'xlarge')};
            }
          }
        `}

      @media screen and (min-width: ${breakpoints3Columns}) {
        &:before {
          margin-left: ${column2Position(ELayout.ThreeColLayout, breakpointFor3ColumnLayout)};
        }
      }
    `;
};

const StyledSectionHeader = styled(Headline)`
  overflow: hidden;
  text-align: center;
  font-size: 1.4rem;
  margin: 1.6rem 1.2rem;
  font-family: ${fontFamily.primary};
  color: ${warmGrey.X};

  &:before,
  &:after {
    background-color: ${warmGrey.V};
    content: '';
    display: inline-block;
    height: 0.1rem;
    position: relative;
    vertical-align: middle;
    width: 50%;
  }

  &:before {
    right: 0.8rem;
    margin-left: -50%;
  }

  &:after {
    left: 0.8rem;
    margin-right: -50%;
  }

  ${columnAlignmentLayout};
`;

const SectionHeader = props => /*#__PURE__*/React.createElement(StyledSectionHeader, {
  className: props.className,
  isInDropdown: props.isInDropdown,
  twoColumnAtBreakpoint: props.twoColumnAtBreakpoint,
  threeColumnAtBreakpoint: props.threeColumnAtBreakpoint
}, props.children);

const TextSectionHeader = props => {
  const {
    text,
    count,
    shouldShowZeros,
    ...baseProps
  } = props;
  return /*#__PURE__*/React.createElement(SectionHeader, baseProps, /*#__PURE__*/React.createElement(HeaderLabelText, {
    text: text,
    count: count,
    shouldShowZeros: shouldShowZeros
  }));
};
const DateSectionHeader = props => {
  const {
    date,
    ...baseProps
  } = props;
  return /*#__PURE__*/React.createElement(SectionHeader, baseProps, /*#__PURE__*/React.createElement(HeaderLabelDate, {
    date: props.date
  }));
};

export default SectionHeader;
export { DateSectionHeader, TextSectionHeader };
//# sourceMappingURL=index.js.map
